import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { listAdminSessions, resendReportEmail } from "@/lib/admin.functions";
import { listAdminBookings } from "@/lib/booking.functions";
import { listAdminBatches } from "@/lib/partner-codes.functions";
import { listAdminNotifications, markNotificationsRead } from "@/lib/notifications.functions";
import { getReviewQueue } from "@/lib/admin-review.functions";
import { getYesterdayPulse } from "@/lib/admin-pulse.functions";
import { YesterdayPulse } from "@/components/AdminPulse";
import { muteAnalyticsForAdmin, unmuteAnalyticsForAdmin } from "@/lib/analytics";
import { reviewPath } from "@/lib/admin-review";
import { AdminFeedList } from "@/components/AdminFeedList";
import { listAdminReviews, setReviewApproved, deleteReview } from "@/lib/reviews.functions";
import { getAdminAnalytics } from "@/lib/analytics.functions";
import { AnalyticsPanel } from "@/components/AnalyticsPanel";
import { TestContentPanel } from "@/components/TestContentPanel";
import { ReportsContentPanel } from "@/components/ReportsContentPanel";
import { PartnerCodesPanel } from "@/components/PartnerCodesPanel";
import { RefundsPanel } from "@/components/RefundsPanel";
import TutorCommissionsPanel from "@/components/TutorCommissionsPanel";
import SchoolAccessPanel from "@/components/SchoolAccessPanel";
import EduReferencePanel from "@/components/EduReferencePanel";
import EduConsultationsPanel from "@/components/EduConsultationsPanel";
import PartnerApplicationsPanel from "@/components/PartnerApplicationsPanel";
import MiniTestGuidePanel from "@/components/MiniTestGuidePanel";
import MapPartnersPanel, { UNSEEN_QUERY_KEY } from "@/components/MapPartnersPanel";
import MapRequestsPanel from "@/components/MapRequestsPanel";
import BlogPanel from "@/components/BlogPanel";
import { countUnseenMapPartners } from "@/lib/map-partners.functions";
import PromoCodesPanel from "@/components/PromoCodesPanel";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { downloadXlsx } from "@/lib/xlsx";
import { isTestRecord } from "@/lib/test-records";
import { Download } from "lucide-react";

export const Route = createFileRoute("/admin")({
  head: () => ({
    meta: [{ title: "Админ — Yolutap" }, { name: "robots", content: "noindex" }],
  }),
  component: AdminPage,
});

const STATUS_VARIANT: Record<string, string> = {
  complete: "bg-emerald-500/10 text-emerald-700 border-emerald-500/30",
  in_progress: "bg-amber-500/10 text-amber-700 border-amber-500/30",
  pending_payment: "bg-muted text-muted-foreground border-border",
  expired: "bg-rose-500/10 text-rose-700 border-rose-500/30",
};

const BOOKING_VARIANT: Record<string, string> = {
  new: "bg-amber-500/10 text-amber-700 border-amber-500/30",
  contacted: "bg-blue-500/10 text-blue-700 border-blue-500/30",
  scheduled: "bg-emerald-500/10 text-emerald-700 border-emerald-500/30",
  done: "bg-muted text-muted-foreground border-border",
  cancelled: "bg-rose-500/10 text-rose-700 border-rose-500/30",
};

const SERVICE_TYPE_LABEL: Record<string, string> = {
  report_review: "Разбор отчёта",
  study_abroad: "Обучение за рубежом",
};
const SERVICE_TYPE_VARIANT: Record<string, string> = {
  report_review: "bg-teal-500/10 text-teal-700 border-teal-500/30",
  study_abroad: "bg-amber-500/10 text-amber-700 border-amber-500/30",
};

function AdminPage() {
  const navigate = useNavigate();
  const [authChecked, setAuthChecked] = useState(false);

  useEffect(() => {
    supabase.auth.getSession().then(async ({ data }) => {
      if (!data.session) {
        // После входа — обратно сюда же (вкладка и анкета из адреса).
        window.location.replace(`/auth?next=${encodeURIComponent(window.location.pathname + window.location.search)}`);
        return;
      }
      // Session present is not enough — require the admin role (user_roles).
      // Non-admins (students/parents) are bounced to their cabinet. Server
      // functions here are already role-gated; this closes the client UX hole.
      const { data: adminRole } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", data.session.user.id)
        .eq("role", "admin")
        .maybeSingle();
      if (!adminRole) {
        navigate({ to: "/cabinet" });
        return;
      }
      // Вошедший админ не попадает в Google Analytics ни здесь, ни на сайте (src/lib/analytics).
      muteAnalyticsForAdmin();
      setAuthChecked(true);
    });
  }, [navigate]);

  const fetchSessions = useServerFn(listAdminSessions);
  const resendReport = useServerFn(resendReportEmail);
  const [resendingId, setResendingId] = useState<string | null>(null);
  async function handleResend(id: string) {
    setResendingId(id);
    try {
      await resendReport({ data: { session_id: id } });
      alert("Письмо поставлено в очередь");
    } catch (e) {
      alert(e instanceof Error ? e.message : "Ошибка");
    } finally {
      setResendingId(null);
    }
  }
  const fetchBookings = useServerFn(listAdminBookings);
  const fetchNotifs = useServerFn(listAdminNotifications);
  // «Ждут проверки: N» — новые анкеты карты и правки из кабинета (/admin/review).
  const fetchReviewQueue = useServerFn(getReviewQueue);
  const reviewQ = useQuery({ queryKey: ["admin-review-queue"], queryFn: () => fetchReviewQueue({ data: {} }), enabled: authChecked, refetchInterval: 60_000 });
  const fetchPulse = useServerFn(getYesterdayPulse);
  const markReadFn = useServerFn(markNotificationsRead);
  const fetchReviews = useServerFn(listAdminReviews);
  const setApprovedFn = useServerFn(setReviewApproved);
  const deleteReviewFn = useServerFn(deleteReview);
  const qc = useQueryClient();

  // Тестовые записи (test@, test-kk, ZZ…) скрыты по умолчанию — один флажок на
  // Сессии, Уведомления, Возвраты и Аналитику (src/lib/test-records.ts).
  const [hideTest, setHideTest] = useState(true);
  // «Вчера» — старты, завершения, оплаты, анкеты, клики, без капчи, вузы; рядом среднее за 7 дней.
  // Тестовые — по тому же флажку «Скрыть тестовые».
  const pulseQ = useQuery({ queryKey: ["admin-pulse", hideTest], queryFn: () => fetchPulse({ data: { hideTest } }), enabled: authChecked, staleTime: 10 * 60_000 });
  // Source filter for the sessions list: "" = all, else a batch id.
  const [sessionSource, setSessionSource] = useState<string>("");
  const [sessionLimit, setSessionLimit] = useState(200);
  const sessionsQ = useQuery({
    queryKey: ["admin-sessions", sessionSource, sessionLimit],
    queryFn: () =>
      fetchSessions({ data: (sessionSource ? { batch_id: sessionSource, limit: sessionLimit } : { limit: sessionLimit }) as never }),
    enabled: authChecked,
    placeholderData: (prev) => prev,
  });
  const allSessions = sessionsQ.data?.sessions ?? [];
  const shownSessions = hideTest ? allSessions.filter((x) => !isTestRecord([x.parent_email, x.child_name])) : allSessions;
  const fetchBatchesForFilter = useServerFn(listAdminBatches);
  const batchesFilterQ = useQuery({
    queryKey: ["partner-batches"],
    queryFn: () => fetchBatchesForFilter({ data: undefined as never }),
    enabled: authChecked,
  });
  const bookingsQ = useQuery({
    queryKey: ["admin-bookings"],
    queryFn: () => fetchBookings({ data: undefined as never }),
    enabled: authChecked,
  });
  const notifsQ = useQuery({
    queryKey: ["admin-notifs"],
    queryFn: () => fetchNotifs({ data: undefined as never }),
    enabled: authChecked,
    refetchInterval: 30_000,
  });
  // Бейдж «Анкеты»: непросмотренные анкеты карты. unseen = null — колонки
  // admin_viewed_at ещё нет (до наката миграции), бейдж не показывается.
  const fetchUnseenPartners = useServerFn(countUnseenMapPartners);
  const unseenPartnersQ = useQuery({
    queryKey: UNSEEN_QUERY_KEY,
    queryFn: () => fetchUnseenPartners({ data: undefined as never }),
    enabled: authChecked,
    refetchInterval: 30_000,
    retry: false,
  });
  const unseenPartners = unseenPartnersQ.data?.unseen ?? null;

  // Вкладка и анкета из адреса: /admin?tab=map-partners&partner=<id> — ссылка
  // из письма о новой анкете. Читаем после монтирования (страница клиентская).
  const [tab, setTab] = useState("notifications");
  const [focusPartner, setFocusPartner] = useState<string | null>(null);
  // /admin?tab=map-requests&request=<id> — ссылка из письма о заявке родителя.
  const [focusRequest, setFocusRequest] = useState<string | null>(null);
  useEffect(() => {
    const sp = new URLSearchParams(window.location.search);
    const t = sp.get("tab");
    if (t) setTab(t);
    const pid = sp.get("partner");
    if (pid && /^[0-9a-f-]{36}$/i.test(pid)) setFocusPartner(pid);
    const rid = sp.get("request");
    if (rid && /^[0-9a-f-]{36}$/i.test(rid)) setFocusRequest(rid);
  }, []);

  const fetchAnalytics = useServerFn(getAdminAnalytics);
  const analyticsQ = useQuery({
    queryKey: ["admin-analytics", hideTest],
    queryFn: () => fetchAnalytics({ data: { hideTest } }),
    enabled: authChecked,
  });
  const reviewsQ = useQuery({
    queryKey: ["admin-reviews"],
    queryFn: () => fetchReviews({ data: undefined as never }),
    enabled: authChecked,
  });

  const [reviewBusy, setReviewBusy] = useState<string | null>(null);
  async function approveReview(id: string, approved: boolean) {
    setReviewBusy(id);
    try {
      await setApprovedFn({ data: { id, approved } });
      qc.invalidateQueries({ queryKey: ["admin-reviews"] });
    } catch (e) {
      alert(e instanceof Error ? e.message : "Ошибка");
    } finally {
      setReviewBusy(null);
    }
  }
  async function removeReview(id: string) {
    if (!confirm("Удалить отзыв навсегда?")) return;
    setReviewBusy(id);
    try {
      await deleteReviewFn({ data: { id } });
      qc.invalidateQueries({ queryKey: ["admin-reviews"] });
    } catch (e) {
      alert(e instanceof Error ? e.message : "Ошибка");
    } finally {
      setReviewBusy(null);
    }
  }

  async function markAllRead() {
    await markReadFn({ data: { all: true } });
    qc.invalidateQueries({ queryKey: ["admin-notifs"] });
  }
  async function markOneRead(id: string) {
    await markReadFn({ data: { ids: [id] } });
    qc.invalidateQueries({ queryKey: ["admin-notifs"] });
  }

  const signOut = async () => {
    unmuteAnalyticsForAdmin();
    await supabase.auth.signOut();
    navigate({ to: "/auth" });
  };

  if (!authChecked) {
    return <div className="min-h-screen flex items-center justify-center">...</div>;
  }

  const err = sessionsQ.error || bookingsQ.error;

  return (
    <div className="min-h-screen bg-background p-6">
      <div className="mx-auto max-w-6xl space-y-6">
        <header className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold">Админка</h1>
            {notifsQ.data && notifsQ.data.unread > 0 && (
              // Кнопка (28.10.2026): открывает вкладку ленты и прокручивает к ней.
              <button
                type="button"
                data-testid="admin-unread-badge"
                className="rounded-full bg-rose-500 px-3 py-1 text-xs font-bold text-white hover:bg-rose-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-rose-500"
                onClick={() => {
                  setTab("notifications");
                  requestAnimationFrame(() => document.getElementById("admin-feed")?.scrollIntoView({ behavior: "smooth", block: "start" }));
                }}
              >
                {notifsQ.data.unread} новое
              </button>
            )}
          </div>
          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={hideTest} onChange={(e) => setHideTest(e.target.checked)} />
              Скрыть тестовые
            </label>
            <Button variant="outline" size="sm" onClick={signOut}>
              Выйти
            </Button>
          </div>
        </header>

        {reviewQ.data && reviewQ.data.count > 0 && (
          <a
            href={reviewQ.data.first ? reviewPath(reviewQ.data.first) : "/admin/review"}
            className="flex items-center justify-between gap-3 rounded-xl border-2 border-amber-500 bg-amber-50 px-4 py-3 font-bold text-amber-900"
          >
            <span>Ждут проверки: {reviewQ.data.count}</span>
            <span>Проверить →</span>
          </a>
        )}

        <YesterdayPulse q={pulseQ} />

        {err && (
          <Card className="border-destructive">
            <CardContent className="pt-6">
              <p className="text-sm text-destructive">
                {/* Map Safari's "Load failed" (cancelled fetch —
                    see src/lib/sentry-config.ts:334) and other
                    transport errors to a friendly copy. This is a
                    query error surface (sessionsQ / bookingsQ), not
                    a mutation, so no timeout / networkMode retrofit
                    is applied here — React Query's built-in retry
                    handles transient errors on queries. */}
                {err instanceof Error &&
                /load failed|failed to fetch|networkerror/i.test(err.message)
                  ? "Не удалось загрузить данные. Проверьте соединение и обновите страницу."
                  : err instanceof Error
                    ? err.message
                    : "Ошибка"}
              </p>
              <p className="text-xs text-muted-foreground mt-2">
                Если у вас нет роли admin — обратитесь к разработчику, чтобы её выдали через
                user_roles.
              </p>
            </CardContent>
          </Card>
        )}

        <Tabs value={tab} onValueChange={setTab}>
          {/* 15 вкладок в строку не помещаются — переносятся, без прокрутки вбок. */}
          <TabsList className="h-auto flex-wrap justify-start gap-1">
            <TabsTrigger value="notifications">
              Уведомления {notifsQ.data && notifsQ.data.unread > 0 && `· ${notifsQ.data.unread}`}
            </TabsTrigger>
            <TabsTrigger value="sessions">
              Сессии {sessionsQ.data && `(${sessionsQ.data.total})`}
            </TabsTrigger>
            <TabsTrigger value="bookings">
              Заявки {bookingsQ.data && `(${bookingsQ.data.bookings.length})`}
            </TabsTrigger>
            <TabsTrigger value="analytics">Аналитика</TabsTrigger>
            <TabsTrigger value="content">Вопросы</TabsTrigger>
            <TabsTrigger value="reports">Отчёты</TabsTrigger>
            <TabsTrigger value="reviews">
              Отзывы {reviewsQ.data && reviewsQ.data.pending > 0 && `· ${reviewsQ.data.pending}`}
            </TabsTrigger>
            <TabsTrigger value="partner-codes">Партнёр-коды</TabsTrigger>
            <TabsTrigger value="promo-codes">Промокоды</TabsTrigger>
            <TabsTrigger value="refunds">Возвраты</TabsTrigger>
            <TabsTrigger value="commissions">Комиссии</TabsTrigger>
            <TabsTrigger value="schools">Школы</TabsTrigger>
            <TabsTrigger value="edu">Образование</TabsTrigger>
            <TabsTrigger value="edu-consult">Консультации EDU</TabsTrigger>
            <TabsTrigger value="partner-applications">Заявки партнёрам</TabsTrigger>
            <TabsTrigger value="mailings">Рассылки</TabsTrigger>
            <TabsTrigger value="map-partners">
              Анкеты
              {unseenPartners !== null && unseenPartners > 0 && (
                <Badge className="ml-1.5 h-5 min-w-5 bg-rose-500 px-1.5 text-white">{unseenPartners}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="map-requests">Заявки родителей</TabsTrigger>
            <TabsTrigger value="blog">Блог</TabsTrigger>
          </TabsList>

          <TabsContent value="blog" className="mt-4">
            {authChecked && <BlogPanel />}
          </TabsContent>

          <TabsContent value="map-partners" className="mt-4">
            {authChecked && <MapPartnersPanel focusId={focusPartner} />}
          </TabsContent>

          <TabsContent value="map-requests" className="mt-4">
            {authChecked && <MapRequestsPanel focusId={focusRequest} />}
          </TabsContent>

          <TabsContent value="edu-consult" className="mt-4">
            {authChecked && <EduConsultationsPanel />}
          </TabsContent>

          <TabsContent value="partner-applications" className="mt-4">
            {authChecked && <PartnerApplicationsPanel />}
          </TabsContent>

          <TabsContent value="mailings" className="mt-4">
            {authChecked && <MiniTestGuidePanel />}
          </TabsContent>

          <TabsContent value="edu" className="mt-4">
            {authChecked && <EduReferencePanel />}
          </TabsContent>

          <TabsContent value="promo-codes" className="mt-4">
            {authChecked && <PromoCodesPanel />}
          </TabsContent>

          <TabsContent value="partner-codes" className="mt-4">
            {authChecked && <PartnerCodesPanel />}
          </TabsContent>

          <TabsContent value="refunds" className="mt-4">
            {authChecked && <RefundsPanel hideTest={hideTest} />}
          </TabsContent>

          <TabsContent value="commissions" className="mt-4">
            {authChecked && <TutorCommissionsPanel />}
          </TabsContent>

          <TabsContent value="schools" className="mt-4">
            {authChecked && <SchoolAccessPanel />}
          </TabsContent>

          <TabsContent value="reports" className="mt-4">
            {authChecked && <ReportsContentPanel />}
          </TabsContent>

          <TabsContent value="content" className="mt-4">
            {authChecked && <TestContentPanel />}
          </TabsContent>

          <TabsContent value="analytics" className="mt-4">
            {analyticsQ.isLoading && <p className="text-sm text-muted-foreground">Загрузка...</p>}
            {analyticsQ.error && <p className="text-sm text-destructive">{String((analyticsQ.error as Error).message)}</p>}
            {analyticsQ.data && <AnalyticsPanel data={analyticsQ.data} />}
          </TabsContent>

          <TabsContent value="notifications" className="mt-4">
            <Card id="admin-feed" className="scroll-mt-4">
              <CardHeader className="flex-row items-center justify-between">
                <CardTitle className="text-base">Лента уведомлений</CardTitle>
                {notifsQ.data && notifsQ.data.unread > 0 && (
                  <Button size="sm" variant="outline" onClick={markAllRead}>
                    Прочитать все
                  </Button>
                )}
              </CardHeader>
              <CardContent>
                {notifsQ.isLoading && <p className="text-sm text-muted-foreground">Загрузка...</p>}
                {notifsQ.data && notifsQ.data.rows.length === 0 && (
                  <p className="text-sm text-muted-foreground py-6 text-center">
                    Уведомлений пока нет
                  </p>
                )}
                {notifsQ.data && notifsQ.data.rows.length > 0 && (
                  <AdminFeedList
                    rows={notifsQ.data.rows.filter((n) => !hideTest || !isTestRecord(`${n.title} ${n.body ?? ""}`.split(/[\s·—,|:()]+/)))}
                    onRead={markOneRead}
                  />
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="sessions" className="mt-4">
            <Card>
              <CardHeader className="flex-row items-center justify-between gap-3">
                <CardTitle className="text-base">
                  Сессии{" "}
                  {sessionsQ.data && (
                    <span className="text-sm font-normal text-muted-foreground">
                      · показано {shownSessions.length} из {sessionsQ.data.total}
                      {hideTest && allSessions.length !== shownSessions.length && ` · скрыто тестовых: ${allSessions.length - shownSessions.length}`}
                    </span>
                  )}
                </CardTitle>
                <div className="flex items-center gap-2">
                  <select
                    value={sessionSource}
                    onChange={(e) => setSessionSource(e.target.value)}
                    className="h-8 rounded-md border bg-background px-2 text-xs"
                    aria-label="Источник"
                  >
                    <option value="">Все источники</option>
                    {(batchesFilterQ.data ?? []).map((b) => (
                      <option key={b.id} value={b.id}>
                        {b.partner_label}
                      </option>
                    ))}
                  </select>
                  {sessionsQ.data && sessionsQ.data.sessions.length > 0 && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        downloadXlsx(
                          `sessions_${new Date().toISOString().slice(0, 10)}.xlsx`,
                          shownSessions.map((s) => ({
                            ...s,
                            source:
                              s.source_kind === "partner"
                                ? s.source_label ?? ""
                                : s.source_kind === "paid"
                                  ? "Ödənişli"
                                  : "Birbaşa",
                          })),
                          [
                            { key: "created_at", header: "created_at" },
                            { key: "child_name", header: "child_name" },
                            { key: "parent_email", header: "parent_email" },
                            { key: "age_group", header: "age_group" },
                            { key: "language_key", header: "language" },
                            { key: "source", header: "source" },
                            { key: "status", header: "status" },
                            { key: "completed_at", header: "completed_at" },
                            { key: "access_token", header: "access_token", text: true },
                          ],
                          "sessions",
                        );
                      }}
                    >
                      <Download className="h-3.5 w-3.5 mr-1.5" /> Excel
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent className="overflow-x-auto">
                {sessionsQ.isLoading && (
                  <p className="text-sm text-muted-foreground">Загрузка...</p>
                )}
                {sessionsQ.data && (
                  <table className="w-full min-w-[680px] text-sm">
                    <thead>
                      <tr className="text-left border-b text-xs uppercase text-muted-foreground">
                        <th className="py-2 pr-3">Создана</th>
                        <th className="py-2 pr-3">Ребёнок</th>
                        <th className="py-2 pr-3">Email</th>
                        <th className="py-2 pr-3">Возраст</th>
                        <th className="py-2 pr-3">Источник</th>
                        <th className="py-2 pr-3">Lang</th>
                        <th className="py-2 pr-3">Статус</th>
                        <th className="py-2 pr-3">Завершено</th>
                        <th className="py-2 pr-3">Действия</th>
                      </tr>
                    </thead>
                    <tbody>
                      {shownSessions.map((s) => (
                        <tr key={s.id} className="border-b last:border-0">
                          <td className="py-2 pr-3 whitespace-nowrap text-xs">
                            {new Date(s.created_at).toLocaleString("ru-RU")}
                          </td>
                          <td className="py-2 pr-3 font-medium">{s.child_name}</td>
                          <td className="py-2 pr-3 text-muted-foreground">{s.parent_email || "—"}</td>
                          <td className="py-2 pr-3">{s.age_group}</td>
                          <td className="py-2 pr-3 text-xs">
                            {s.source_kind === "partner" ? (
                              <Badge variant="outline" className="border-primary/40 text-primary">
                                {s.source_label || "—"}
                              </Badge>
                            ) : (
                              <span className="text-muted-foreground">
                                {s.source_kind === "paid" ? "Ödənişli" : "Birbaşa"}
                              </span>
                            )}
                          </td>
                          <td className="py-2 pr-3 uppercase">{s.language_key}</td>
                          <td className="py-2 pr-3">
                            <Badge variant="outline" className={STATUS_VARIANT[s.status] ?? ""}>
                              {s.status}
                            </Badge>
                          </td>
                          <td className="py-2 pr-3 text-xs text-muted-foreground">
                            {s.completed_at
                              ? new Date(s.completed_at).toLocaleString("ru-RU")
                              : "—"}
                          </td>
                          <td className="py-2 pr-3 space-x-2 whitespace-nowrap">
                            <Link
                              to="/session/$token"
                              params={{ token: s.access_token }}
                              className="text-primary text-xs underline"
                            >
                              сессия
                            </Link>
                            {s.status === "complete" && (
                              <>
                                <Link
                                  to="/session/$token/result"
                                  params={{ token: s.access_token }}
                                  className="text-primary text-xs underline"
                                >
                                  отчёт
                                </Link>
                                <button
                                  onClick={() => handleResend(s.id)}
                                  disabled={resendingId === s.id}
                                  className="text-primary text-xs underline disabled:opacity-50"
                                >
                                  {resendingId === s.id ? "..." : "переотпр. email"}
                                </button>
                              </>
                            )}
                          </td>
                        </tr>
                      ))}
                      {shownSessions.length === 0 && (
                        <tr>
                          <td colSpan={9} className="py-6 text-center text-muted-foreground">
                            Сессий пока нет
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                )}
                {sessionsQ.data && !sessionSource && sessionsQ.data.sessions.length < sessionsQ.data.total && (
                  <div className="pt-3">
                    <Button size="sm" variant="outline" disabled={sessionsQ.isFetching} onClick={() => setSessionLimit((n) => n + 200)}>
                      {sessionsQ.isFetching ? "Загрузка..." : "Показать ещё 200"}
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="bookings" className="mt-4">
            <Card>
              <CardHeader className="flex-row items-center justify-between">
                <CardTitle className="text-base">Заявки на консультацию</CardTitle>
                {bookingsQ.data && bookingsQ.data.bookings.length > 0 && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      downloadXlsx(
                        `bookings_${new Date().toISOString().slice(0, 10)}.xlsx`,
                        bookingsQ.data!.bookings,
                        [
                          { key: "created_at", header: "created_at" },
                          { key: "service_type", header: "service_type" },
                          { key: "parent_name", header: "parent_name" },
                          { key: "parent_email", header: "parent_email" },
                          // Телефон текстом: «+994…» Excel бы съел, а «055…»
                          // потерял бы ноль.
                          { key: "phone", header: "phone", text: true },
                          { key: "child_name", header: "child_name" },
                          { key: "preferred_time", header: "preferred_time" },
                          { key: "notes", header: "notes" },
                          { key: "status", header: "status" },
                          { key: "access_token", header: "access_token", text: true },
                        ],
                        "bookings",
                      );
                    }}
                  >
                    <Download className="h-3.5 w-3.5 mr-1.5" /> Excel
                  </Button>
                )}
              </CardHeader>
              <CardContent className="overflow-x-auto">
                {bookingsQ.isLoading && (
                  <p className="text-sm text-muted-foreground">Загрузка...</p>
                )}
                {bookingsQ.data && (
                  <table className="w-full min-w-[680px] text-sm">
                    <thead>
                      <tr className="text-left border-b text-xs uppercase text-muted-foreground">
                        <th className="py-2 pr-3">Дата</th>
                        <th className="py-2 pr-3">Тип</th>
                        <th className="py-2 pr-3">Родитель</th>
                        <th className="py-2 pr-3">Email / Телефон</th>
                        <th className="py-2 pr-3">Ребёнок</th>
                        <th className="py-2 pr-3">Время</th>
                        <th className="py-2 pr-3">Комментарий</th>
                        <th className="py-2 pr-3">Статус</th>
                        <th className="py-2 pr-3">Сессия</th>
                      </tr>
                    </thead>
                    <tbody>
                      {bookingsQ.data.bookings.map((b) => (
                        <tr key={b.id} className="border-b last:border-0 align-top">
                          <td className="py-2 pr-3 whitespace-nowrap text-xs">
                            {new Date(b.created_at).toLocaleString("ru-RU")}
                          </td>
                          <td className="py-2 pr-3">
                            <Badge
                              variant="outline"
                              className={SERVICE_TYPE_VARIANT[b.service_type] ?? ""}
                            >
                              {SERVICE_TYPE_LABEL[b.service_type] ?? b.service_type}
                            </Badge>
                          </td>
                          <td className="py-2 pr-3 font-medium">{b.parent_name}</td>
                          <td className="py-2 pr-3 text-xs">
                            {b.parent_email && <div>{b.parent_email}</div>}
                            {b.phone && <div className="text-muted-foreground">{b.phone}</div>}
                            {!b.parent_email && !b.phone && "—"}
                          </td>
                          <td className="py-2 pr-3">{b.child_name ?? "—"}</td>
                          <td className="py-2 pr-3 text-xs">{b.preferred_time ?? "—"}</td>
                          <td className="py-2 pr-3 text-xs max-w-xs">{b.notes ?? "—"}</td>
                          <td className="py-2 pr-3">
                            <Badge variant="outline" className={BOOKING_VARIANT[b.status] ?? ""}>
                              {b.status}
                            </Badge>
                          </td>
                          <td className="py-2 pr-3 whitespace-nowrap">
                            {b.access_token && (
                              <Link
                                to="/session/$token/result"
                                params={{ token: b.access_token }}
                                className="text-primary text-xs underline"
                              >
                                отчёт
                              </Link>
                            )}
                          </td>
                        </tr>
                      ))}
                      {bookingsQ.data.bookings.length === 0 && (
                        <tr>
                          <td colSpan={9} className="py-6 text-center text-muted-foreground">
                            Заявок пока нет
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="reviews" className="mt-4">
            <Card>
              <CardHeader className="flex-row items-center justify-between">
                <CardTitle className="text-base">
                  Отзывы на модерации{" "}
                  {reviewsQ.data && (
                    <span className="text-xs font-normal text-muted-foreground">
                      ({reviewsQ.data.pending} ждут · {reviewsQ.data.rows.length} всего)
                    </span>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent className="overflow-x-auto">
                {reviewsQ.isLoading && (
                  <p className="text-sm text-muted-foreground">Загрузка...</p>
                )}
                {reviewsQ.data && (
                  <table className="w-full min-w-[680px] text-sm">
                    <thead>
                      <tr className="text-left border-b text-xs uppercase text-muted-foreground">
                        <th className="py-2 pr-3">Дата</th>
                        <th className="py-2 pr-3">Lang</th>
                        <th className="py-2 pr-3">★</th>
                        <th className="py-2 pr-3">Имя</th>
                        <th className="py-2 pr-3">Возраст</th>
                        <th className="py-2 pr-3">Текст</th>
                        <th className="py-2 pr-3">Статус</th>
                        <th className="py-2 pr-3">Действия</th>
                      </tr>
                    </thead>
                    <tbody>
                      {reviewsQ.data.rows.map((r) => (
                        <tr key={r.id} className="border-b last:border-0 align-top">
                          <td className="py-2 pr-3 whitespace-nowrap text-xs">
                            {new Date(r.created_at).toLocaleString("ru-RU")}
                          </td>
                          <td className="py-2 pr-3 uppercase text-xs">{r.lang}</td>
                          <td className="py-2 pr-3 whitespace-nowrap">
                            <span className="text-amber-500">{"★".repeat(r.rating)}</span>
                            <span className="text-muted-foreground/40">
                              {"★".repeat(5 - r.rating)}
                            </span>
                          </td>
                          <td className="py-2 pr-3 font-medium whitespace-nowrap">{r.name}</td>
                          <td className="py-2 pr-3 text-xs whitespace-nowrap">
                            {r.child_age ?? "—"}
                          </td>
                          <td className="py-2 pr-3 text-xs max-w-md whitespace-pre-wrap">
                            {r.text}
                          </td>
                          <td className="py-2 pr-3">
                            <Badge
                              variant="outline"
                              className={
                                r.approved
                                  ? "bg-emerald-500/10 text-emerald-700 border-emerald-500/30"
                                  : "bg-amber-500/10 text-amber-700 border-amber-500/30"
                              }
                            >
                              {r.approved ? "опубликован" : "на модерации"}
                            </Badge>
                          </td>
                          <td className="py-2 pr-3 space-x-2 whitespace-nowrap">
                            {!r.approved ? (
                              <button
                                onClick={() => approveReview(r.id, true)}
                                disabled={reviewBusy === r.id}
                                className="text-emerald-600 text-xs underline disabled:opacity-50"
                              >
                                {reviewBusy === r.id ? "..." : "одобрить"}
                              </button>
                            ) : (
                              <button
                                onClick={() => approveReview(r.id, false)}
                                disabled={reviewBusy === r.id}
                                className="text-muted-foreground text-xs underline disabled:opacity-50"
                              >
                                {reviewBusy === r.id ? "..." : "снять с публикации"}
                              </button>
                            )}
                            <button
                              onClick={() => removeReview(r.id)}
                              disabled={reviewBusy === r.id}
                              className="text-rose-600 text-xs underline disabled:opacity-50"
                            >
                              удалить
                            </button>
                          </td>
                        </tr>
                      ))}
                      {reviewsQ.data.rows.length === 0 && (
                        <tr>
                          <td colSpan={8} className="py-6 text-center text-muted-foreground">
                            Отзывов пока нет
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

